import React, { useRef, useState, useEffect } from 'react';

import { AssessmentDashboardYearLevel } from '../../../api/models/assessment-dashboard-year-level';
import { SortOptionsType } from '../../TitleSection/SortDropdown';

interface BenchmarkChartProps {
	title: string;
	yearLevels?: AssessmentDashboardYearLevel[];
	selectedViewOption: SortOptionsType;
}

const BenchmarkChart: React.FC<BenchmarkChartProps> = ({ title, yearLevels, selectedViewOption }) => {
	const titleRef = useRef<HTMLParagraphElement>(null);
	const [useTitleHeightOffset, setUseTitleHeightOffset] = useState(false);

	useEffect(() => {
		if (titleRef.current) {
			const element = titleRef.current;

			// Add a small delay to ensure DOM is fully rendered
			const checkWrapping = () => {
				// Get computed styles to check for actual text wrapping
				const computedStyle = window.getComputedStyle(element);
				const lineHeight = parseFloat(computedStyle.lineHeight);
				const actualHeight = element.offsetHeight;

				// If the actual height is more than 1.2x the line height, text has wrapped
				const hasWrapped = actualHeight > lineHeight * 1.2;

				setUseTitleHeightOffset(hasWrapped);
			};

			// Check immediately
			checkWrapping();

			// Also check after a small delay to ensure proper rendering
			const timeoutId = setTimeout(checkWrapping, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [title]);
	return (
		<article className={`assessment-benchmark-card ${useTitleHeightOffset ? 'title-height-offset' : ''}`}>
			<p ref={titleRef} className="h3 text-shades-800 text-center mb-4 card-title">
				{title}
			</p>

			<div className="d-flex mb-4">
				{yearLevels?.map((yearLevelItem, yearLevelIndex) => (
					<div
						className="d-flex gap-3 align-items-start justify-content-center flex-grow-1 pt-2 mt-1"
						key={`year-level-${yearLevelIndex}`}
					>
						<div className={`label-icon bg-${yearLevelItem?.year?.toLowerCase().replace(/\s+/g, '-')}`}></div>

						<div>
							<p className="h4 text-shades-800 mb-0">
								{/* If selectedViewOption.value is "", meaning "All Classes" is selected, then use yearLevelItem.year,Otherwise if NOT "All Classes" is selected, then use selectedViewOption.label */}
								{selectedViewOption.value ? selectedViewOption.label : yearLevelItem.year || ''}
							</p>

							<span className="body-small text-shades-500">
								{yearLevelItem.totalCount ?? 0} student{yearLevelItem.totalCount === 1 ? '' : 's'}
							</span>
						</div>
					</div>
				))}
			</div>

			<p className="body-small text-shades-500 text-center mb-4"> No. of students</p>

			{/* CHART */}
			<div className="benchmark-chart-wrapper">
				<div className="line-background">
					<div className="line-label label-100">100%</div>
					<div className="line-label label-50">50%</div>
					<div className="line-label label-0">0%</div>

					<div className="line line-100"></div>
					<div className="line line-75"></div>
					<div className="line line-50"></div>
					<div className="line line-25"></div>
					<div className="line line-0"></div>
				</div>

				<div className="chart-content">
					<div className="d-flex align-items-end gap-2 h-100 flex-grow-1">
						{/* Below Benchmark */}
						<div className="year-chart-group">
							{yearLevels
								?.filter((yearLevelItem) => yearLevelItem.belowBenchmarkPercentage !== 0)
								.map((yearLevelItem, yearLevelIndex) => (
									<div
										className="chart-single"
										key={`chart-single-below-${yearLevelIndex}`}
										style={{ height: `${yearLevelItem.belowBenchmarkPercentage ?? 0}%` }}
									>
										<span className="count-text">{yearLevelItem.belowBenchmarkCount ?? 0}</span>

										<span className="percentage-text">
											(
											{yearLevelItem.belowBenchmarkPercentage !== undefined
												? Number.isInteger(yearLevelItem.belowBenchmarkPercentage)
													? yearLevelItem.belowBenchmarkPercentage
													: yearLevelItem.belowBenchmarkPercentage.toFixed(2)
												: ''}
											%)
										</span>

										<div className={`chart-bar bg-${yearLevelItem?.year?.toLowerCase().replace(/\s+/g, '-')}`}></div>
									</div>
								))}

							<span className={`chart-label ${useTitleHeightOffset ? 'title-height-offset' : ''}`}>
								Below benchmark
							</span>
						</div>

						{/* At Benchmark */}
						<div className="year-chart-group">
							{yearLevels
								?.filter((yearLevelItem) => yearLevelItem.atBenchmarkPercentage !== 0)
								.map((yearLevelItem, yearLevelIndex) => (
									<div
										className="chart-single"
										key={`chart-single-at-${yearLevelIndex}`}
										style={{ height: `${yearLevelItem.atBenchmarkPercentage ?? 0}%` }}
									>
										<span className="count-text">{yearLevelItem.atBenchmarkCount ?? 0}</span>

										<span className="percentage-text">
											(
											{yearLevelItem.atBenchmarkPercentage !== undefined
												? Number.isInteger(yearLevelItem.atBenchmarkPercentage)
													? yearLevelItem.atBenchmarkPercentage
													: yearLevelItem.atBenchmarkPercentage.toFixed(2)
												: ''}
											%)
										</span>

										<div className={`chart-bar bg-${yearLevelItem?.year?.toLowerCase().replace(/\s+/g, '-')}`}></div>
									</div>
								))}

							<span className={`chart-label ${useTitleHeightOffset ? 'title-height-offset' : ''}`}>At benchmark</span>
						</div>

						{/* Above Benchmark */}
						<div className="year-chart-group">
							{yearLevels
								?.filter((yearLevelItem) => yearLevelItem.aboveBenchmarkPercentage !== 0)
								.map((yearLevelItem, yearLevelIndex) => (
									<div
										className="chart-single"
										key={`chart-single-above-${yearLevelIndex}`}
										style={{ height: `${yearLevelItem.aboveBenchmarkPercentage ?? 0}%` }}
									>
										<span className="count-text">{yearLevelItem.aboveBenchmarkCount ?? 0}</span>

										<span className="percentage-text">
											(
											{yearLevelItem.aboveBenchmarkPercentage !== undefined
												? Number.isInteger(yearLevelItem.aboveBenchmarkPercentage)
													? yearLevelItem.aboveBenchmarkPercentage
													: yearLevelItem.aboveBenchmarkPercentage.toFixed(2)
												: ''}
											%)
										</span>

										<div className={`chart-bar bg-${yearLevelItem?.year?.toLowerCase().replace(/\s+/g, '-')}`}></div>
									</div>
								))}

							<span className={`chart-label ${useTitleHeightOffset ? 'title-height-offset' : ''}`}>
								Above benchmark
							</span>
						</div>
					</div>
				</div>
			</div>
		</article>
	);
};

export default BenchmarkChart;
